import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/auth';
import { useSmsStatus } from '@/context/SmsStatusContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { SmsStatusIndicator } from '@/components/sms/SmsStatusIndicator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Loader, Search, Filter, CheckSquare, PlusSquare, ArrowDownUp, Info, Bug } from 'lucide-react';
import { hasSuperAdminAccess, hasOrgAdminAccess } from '@/context/auth/types';
import { RestaurantWithSmsStatus } from '@/context/SmsStatusContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { safeToLowerCase, getDisplayName, getEntityId, debugJwtInfo } from '@/utils/display-helpers';
import { debugJwtRoles, logAuthStorageState } from '@/utils/debug';

// Helper function to safely get a display name for a restaurant
const getRestaurantDisplayName = (restaurant: RestaurantWithSmsStatus | null | undefined) => {
  if (!restaurant) return 'Unknown Restaurant';
  return getDisplayName(restaurant, 'id', 'name', 'Restaurant');
};

// Helper function to safely get restaurant ID
const getRestaurantId = (restaurant: RestaurantWithSmsStatus | null | undefined): string => {
  if (!restaurant) return 'unknown-id';
  return getEntityId(restaurant, ['id', 'restaurant_id']);
};


export const SmsManager: React.FC = () => {
  const { user } = useAuth();
  const { 
    getRestaurantListWithSmsStatus, 
    enableRestaurantSms,
    disableRestaurantSms,
    loading,
    error
  } = useSmsStatus();
  
  const isMobile = useIsMobile();
  const [restaurants, setRestaurants] = useState<RestaurantWithSmsStatus[]>([]);
  const [selectedRestaurants, setSelectedRestaurants] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showBulkEnableDialog, setShowBulkEnableDialog] = useState(false);
  const [showBulkDisableDialog, setShowBulkDisableDialog] = useState(false);
  const [selectedRestaurantToModify, setSelectedRestaurantToModify] = useState<string | null>(null);
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  
  // Determine user permissions
  const canEnableSms = user && (hasSuperAdminAccess(user.role) || hasOrgAdminAccess(user.role));
  const canDisableSms = user && hasSuperAdminAccess(user.role);
  
  // Load restaurants data with enhanced logging and memoization
  // Track whether initial fetch has completed to prevent redundant loads
  const [initialFetchComplete, setInitialFetchComplete] = useState(false);

  useEffect(() => {
    const fetchRestaurants = async () => {
      // Skip if we've already loaded the data and user hasn't changed
      if (initialFetchComplete && restaurants.length > 0) {
        return;
      }

      setIsLoading(true);
      try {
        if (user?.organisationId) {
          console.log(`Fetching restaurants for organisation: ${user.organisationId}`);
          console.log(`User role: ${user.role}, JWT debug info:`, debugJwtInfo());
          
          const restaurantsList = await getRestaurantListWithSmsStatus(user.organisationId);
          
          // Log the raw restaurant data
          console.log('Raw restaurant data received:', restaurantsList);
          
          // Check for data integrity issues
          const restaurantsWithMissingIds = restaurantsList.filter(r => !r.id);
          const restaurantsWithMissingNames = restaurantsList.filter(r => !r.name);
          
          if (restaurantsWithMissingIds.length > 0) {
            console.warn('Restaurants with missing IDs:', restaurantsWithMissingIds);
          }
          
          if (restaurantsWithMissingNames.length > 0) {
            console.warn('Restaurants with missing names:', restaurantsWithMissingNames);
          }
          
          setRestaurants(restaurantsList);
          setInitialFetchComplete(true);
        }
      } catch (err) {
        console.error('Error fetching restaurants:', err);
        toast({
          title: "Error fetching restaurants",
          description: "Failed to load restaurant data. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchRestaurants();
  }, [user?.organisationId, getRestaurantListWithSmsStatus, initialFetchComplete, restaurants.length]);
  
  // Filter restaurants based on search term with null-safe operations
  const filteredRestaurants = useMemo(() => {
    return restaurants
      .filter(restaurant => {
        // Null check before calling toLowerCase
        const restaurantName = restaurant?.name || '';
        const searchTermLower = safeToLowerCase(searchTerm);
        return safeToLowerCase(restaurantName).includes(searchTermLower);
      })
      .sort((a, b) => {
        // Null checks for sort
        const nameA = a?.name || '';
        const nameB = b?.name || '';
        
        if (sortDirection === 'asc') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      });
  }, [restaurants, searchTerm, sortDirection]);
  
  // Toggle selection for a restaurant
  const toggleRestaurantSelection = (restaurantId: string) => {
    if (!restaurantId) return;
    
    setSelectedRestaurants(prev => {
      if (prev.includes(restaurantId)) {
        return prev.filter(id => id !== restaurantId);
      } else {
        return [...prev, restaurantId];
      }
    });
  };
  
  // Select or deselect all restaurants
  const toggleSelectAll = () => {
    if (selectedRestaurants.length === filteredRestaurants.length) {
      setSelectedRestaurants([]);
    } else {
      // Filter out any undefined/null IDs
      const validIds = filteredRestaurants
        .map(r => r?.id)
        .filter(Boolean) as string[];
      setSelectedRestaurants(validIds);
    }
  };
  
  // Bulk enable SMS for selected restaurants
  const bulkEnableSms = async () => {
    setShowBulkEnableDialog(false);
    
    if (selectedRestaurants.length === 0) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const restaurantId of selectedRestaurants) {
      if (!restaurantId) {
        failCount++;
        continue;
      }
      
      try {
        const success = await enableRestaurantSms(restaurantId);
        if (success) {
          successCount++;
          // Update local state
          setRestaurants(prev => 
            prev.map(r => r.id === restaurantId ? { ...r, smsEnabled: true } : r)
          );
        } else {
          failCount++;
        }
      } catch (err) {
        console.error(`Error enabling SMS for restaurant ${restaurantId}:`, err);
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast({
        title: "SMS Enabled",
        description: `Successfully enabled SMS for ${successCount} restaurant${successCount > 1 ? 's' : ''}.`,
      });
    }
    
    if (failCount > 0) {
      toast({
        title: "Some operations failed",
        description: `Failed to enable SMS for ${failCount} restaurant${failCount > 1 ? 's' : ''}.`,
        variant: "destructive"
      });
    }
    
    // Clear selection
    setSelectedRestaurants([]);
  };
  
  // Bulk disable SMS for selected restaurants
  const bulkDisableSms = async () => {
    setShowBulkDisableDialog(false);
    
    if (selectedRestaurants.length === 0) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const restaurantId of selectedRestaurants) {
      if (!restaurantId) {
        failCount++;
        continue;
      }
      
      try {
        const success = await disableRestaurantSms(restaurantId);
        if (success) {
          successCount++;
          // Update local state
          setRestaurants(prev => 
            prev.map(r => r.id === restaurantId ? { ...r, smsEnabled: false } : r)
          );
        } else {
          failCount++;
        }
      } catch (err) {
        console.error(`Error disabling SMS for restaurant ${restaurantId}:`, err);
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast({
        title: "SMS Disabled",
        description: `Successfully disabled SMS for ${successCount} restaurant${successCount > 1 ? 's' : ''}.`,
      });
    }
    
    if (failCount > 0) {
      toast({
        title: "Some operations failed",
        description: `Failed to disable SMS for ${failCount} restaurant${failCount > 1 ? 's' : ''}.`,
        variant: "destructive"
      });
    }
    
    // Clear selection
    setSelectedRestaurants([]);
  };
  
  // Handle individual restaurant SMS enable/disable
  const handleSingleRestaurantToggle = (restaurantId: string | undefined, currentStatus: boolean) => {
    if (!restaurantId) return;
    
    setSelectedRestaurantToModify(restaurantId);
    if (currentStatus) {
      setShowDisableDialog(true);
    } else {
      setShowEnableDialog(true);
    }
  };
  
  const confirmSingleEnable = async () => {
    if (!selectedRestaurantToModify) return;
    
    try {
      const success = await enableRestaurantSms(selectedRestaurantToModify);
      if (success) {
        // Update local state
        setRestaurants(prev => 
          prev.map(r => r.id === selectedRestaurantToModify ? { ...r, smsEnabled: true } : r)
        );
        toast({
          title: "SMS Enabled",
          description: "SMS has been successfully enabled for this restaurant.",
        });
      }
    } catch (err) {
      console.error('Error enabling SMS:', err);
      toast({
        title: "Error",
        description: "Failed to enable SMS. Please try again.",
        variant: "destructive"
      });
    } finally {
      setShowEnableDialog(false);
      setSelectedRestaurantToModify(null);
    }
  };
  
  const confirmSingleDisable = async () => {
    if (!selectedRestaurantToModify) return;
    
    try {
      const success = await disableRestaurantSms(selectedRestaurantToModify);
      if (success) {
        // Update local state
        setRestaurants(prev => 
          prev.map(r => r.id === selectedRestaurantToModify ? { ...r, smsEnabled: false } : r)
        );
        toast({
          title: "SMS Disabled",
          description: "SMS has been successfully disabled for this restaurant.",
        });
      }
    } catch (err) {
      console.error('Error disabling SMS:', err);
      toast({
        title: "Error",
        description: "Failed to disable SMS. Please try again.",
        variant: "destructive"
      });
    } finally {
      setShowDisableDialog(false);
      setSelectedRestaurantToModify(null);
    }
  };
  
  // Calculate stats for selected restaurants
  const selectedStats = useMemo(() => {
    const selectedCount = selectedRestaurants.length;
    const enabledCount = selectedRestaurants.reduce((count, id) => {
      const restaurant = restaurants.find(r => r.id === id);
      return restaurant?.smsEnabled ? count + 1 : count;
    }, 0);
    
    return {
      total: selectedCount,
      enabled: enabledCount,
      disabled: selectedCount - enabledCount
    };
  }, [selectedRestaurants, restaurants]);

  if (error) {
    return (
      <div className="text-center p-6">
        <p className="text-red-500">Error loading SMS management: {error}</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Info card */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium">Restaurant SMS Management</h3>
              <p className="text-sm text-muted-foreground">
                SMS messaging can be enabled or disabled for each restaurant. When enabled, 
                standard SMS messaging rates will apply to your organisation's billing account.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Actions and search row */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-2">
          {canEnableSms && selectedRestaurants.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button 
                size="sm" 
                onClick={() => setShowBulkEnableDialog(true)}
                disabled={selectedStats.enabled === selectedStats.total}
              >
                <PlusSquare className="h-4 w-4 mr-1" />
                Enable SMS ({selectedStats.total - selectedStats.enabled})
              </Button>
              
              {canDisableSms && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowBulkDisableDialog(true)}
                  disabled={selectedStats.disabled === selectedStats.total}
                >
                  Disable SMS ({selectedStats.enabled})
                </Button>
              )}
              
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedRestaurants([])}
              >
                Clear selection
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex gap-2">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search restaurants..." 
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <Button
            size="icon"
            variant="outline"
            onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
            title={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
          >
            <ArrowDownUp className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Restaurant list */}
      <div className="rounded-md border">
        <div className="bg-muted/50 p-3 flex items-center border-b">
          <div className="flex items-center">
            <Checkbox 
              id="select-all"
              checked={selectedRestaurants.length > 0 && selectedRestaurants.length === filteredRestaurants.length}
              onCheckedChange={toggleSelectAll}
              className="mr-2"
            />
            <label htmlFor="select-all" className="text-sm font-medium">
              {filteredRestaurants.length} restaurants
            </label>
          </div>
        </div>
        
        <div className="divide-y">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2">Loading restaurants...</span>
            </div>
          ) : filteredRestaurants.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground">
              No restaurants found.
            </div>
          ) : (
            filteredRestaurants.map(restaurant => {
              // Safe access to restaurant ID using the utility function
              const restaurantId = getRestaurantId(restaurant);
              const restaurantName = getRestaurantDisplayName(restaurant);
              const isSelected = selectedRestaurants.includes(restaurantId);
              
              return (
                <div 
                  key={restaurantId} 
                  className={`p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-muted/30 ${
                    isSelected ? 'bg-muted/40' : ''
                  }`}
                >
                  <div className="flex items-center">
                    <Checkbox 
                      id={`restaurant-${restaurantId}`}
                      checked={isSelected}
                      onCheckedChange={() => toggleRestaurantSelection(restaurantId)}
                      className="mr-3"
                    />
                    <div>
                      <label htmlFor={`restaurant-${restaurantId}`} className="font-medium cursor-pointer">
                        {restaurantName}
                      </label>
                      {restaurant.id && restaurant.id !== 'unknown-id' && (
                        <p className="text-xs text-muted-foreground">ID: {restaurant.id}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 ml-8 sm:ml-0">
                    <SmsStatusIndicator 
                      restaurantId={restaurantId}
                      compact={isMobile}
                      showToggle={false}
                      position={`SmsManager-list-${restaurantId}`}
                    />
                    
                    {(canEnableSms || canDisableSms) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-2">
                              <span className="text-sm mr-1 text-muted-foreground hidden sm:inline">
                                {restaurant?.smsEnabled ? "Enabled" : "Disabled"}
                              </span>
                              <Switch
                                checked={restaurant?.smsEnabled || false}
                                onCheckedChange={() => handleSingleRestaurantToggle(restaurantId, restaurant?.smsEnabled || false)}
                                disabled={
                                  (restaurant?.smsEnabled && !canDisableSms) || 
                                  (!restaurant?.smsEnabled && !canEnableSms)
                                }
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {restaurant?.smsEnabled 
                              ? (canDisableSms ? "Disable SMS for this restaurant" : "You don't have permission to disable SMS") 
                              : (canEnableSms ? "Enable SMS for this restaurant" : "You don't have permission to enable SMS")}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      
      {/* Confirmation dialogs */}
      <AlertDialog open={showBulkEnableDialog} onOpenChange={setShowBulkEnableDialog}>
        <AlertDialogContent className={isMobile ? "max-w-[90%]" : ""}>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable SMS for Multiple Restaurants?</AlertDialogTitle>
            <AlertDialogDescription>
              This will activate SMS capabilities for {selectedStats.total - selectedStats.enabled} selected restaurants.
              Standard SMS credit rates will apply to messages sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkEnableSms}>Enable SMS</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <AlertDialog open={showBulkDisableDialog} onOpenChange={setShowBulkDisableDialog}>
        <AlertDialogContent className={isMobile ? "max-w-[90%]" : ""}>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable SMS for Multiple Restaurants?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate SMS capabilities for {selectedStats.enabled} selected restaurants.
              They won't be able to send SMS messages until you enable it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDisableSms}>Disable SMS</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <AlertDialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
        <AlertDialogContent className={isMobile ? "max-w-[90%]" : ""}>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable SMS for this Restaurant?</AlertDialogTitle>
            <AlertDialogDescription>
              This will activate SMS capabilities for this restaurant.
              Standard SMS credit rates will apply to messages sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSingleEnable}>Enable SMS</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent className={isMobile ? "max-w-[90%]" : ""}>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable SMS for this Restaurant?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate SMS capabilities for this restaurant.
              It won't be able to send SMS messages until you enable it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSingleDisable}>Disable SMS</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};